/* eslint-disable no-unused-vars */
import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { BLOCKS } from '@contentful/rich-text-types';
import Siema from 'siema';

import RichText from '../../../Core/RichText';

import {
  Tab,
  ContentWrapper,
  ImageWrapper,
  Heading,
  CardImage,
  Body,
  CarouselControls,
  CarouselBtn,
} from './styles';

// start index of the carousel
const startIndex = 0;

const TabCarousel = ({
  componentStyle = '',
  columnCount = '4',
  data = null,
  selectableTabs = null,
  className = '',
  setActiveColumn = null,
}) => {
  // * Null check for data
  if (!data) return false;

  const body = {
    renderNode: {
      /*
       * Exception for ARIA role="text" being added to axe-core in 4.2, this will currently display as an error in an automated test using either Axe or Lighthouse
       * https://github.com/dequelabs/axe-core/milestone/19
       * https://github.com/dequelabs/axe-core/issues/1597
       */
      // eslint-disable-next-line jsx-a11y/aria-role
      [BLOCKS.PARAGRAPH]: (_, children) => <p role="text">{children}</p>,
    },
  };
  const [currentSlide, setCurrentSlide] = useState(0);
  const carousel = useRef(null);

  function changeSlide(index) {
    carousel.current.goTo(index);
    // when changing slide update the active column
    const currentActiveColumn = selectableTabs[index].id;
    const dataId = data.map(col => col.id);
    const activeDataIdx = dataId.indexOf(currentActiveColumn);
    setActiveColumn(activeDataIdx);
  }

  const initCarousel = () => {
    const instance = new Siema({
      selector: `.siema-tablist`,
      duration: 350,
      easing: 'ease-out',
      perPage: 1,
      startIndex,
      draggable: false,
      multipleDrag: false,
      threshold: 20,
      loop: false,
      rtl: false,
      onChange: () => {
        setCurrentSlide(Math.ceil(carousel.current.currentSlide));
      },
    });

    return instance;
  };

  const destroyCarousel = () => {
    if (carousel.current) carousel.current.destroy(true);
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      carousel.current = initCarousel();
    }
    return () => {
      destroyCarousel();
    };
  }, []);

  return (
    <>
      <div className="siema-tablist">
        {selectableTabs.map(dataItem => {
          const {
            id,
            selectable,
            heading,
            bodyCopy,
            imageDam,
            backgroundStyle,
          } = dataItem;

          return (
            <Tab
              key={id}
              id={
                imageDam
                  ? imageDam.altText.toLowerCase().replace(/\s/g, '-')
                  : id
              }
              className={`${className} 
                ${componentStyle} 
                is-selectable 
                carousel-tab 
                pc-mastercard`}
              as="div"
              columnCount={columnCount}
              selectable={selectable}
            >
              <ContentWrapper
                className={`${className} ${backgroundStyle}`}
                columnCount={columnCount}
              >
                {imageDam && (
                  <ImageWrapper>
                    <CardImage src={imageDam} alt="" />
                  </ImageWrapper>
                )}
                {heading && (
                  <Heading>
                    <RichText document={heading} />
                  </Heading>
                )}
                {bodyCopy && (
                  <Body>
                    <RichText document={bodyCopy} options={body} />
                  </Body>
                )}
              </ContentWrapper>
            </Tab>
          );
        })}
      </div>
      <CarouselControls className="carousel-ctrls">
        <ul className="no-bullets">
          <li key="control-key-left">
            <CarouselBtn
              className="previous-carousel"
              disabled={currentSlide === 0}
              onClick={() => changeSlide(currentSlide - 1)}
            />
          </li>
          <li key="control-key-right">
            <CarouselBtn
              className="next-carousel"
              disabled={currentSlide + 1 === selectableTabs.length}
              onClick={() => changeSlide(currentSlide + 1)}
            />
          </li>
        </ul>
      </CarouselControls>
    </>
  );
};

TabCarousel.propTypes = {
  columnCount: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.shape()),
  selectableTabs: PropTypes.arrayOf(PropTypes.shape()),
  componentStyle: PropTypes.string,
  className: PropTypes.string,
  setActiveColumn: PropTypes.func,
};

export default TabCarousel;
